import IosShareIcon from '@mui/icons-material/IosShare';
import { Box, Button, CircularProgress, Typography, useTheme } from "@mui/material";
import { getOrInitTwilioClient } from 'app/DesktopLayout/twilioClient';
import BoatImage from "assets/images/boat.png";
import { baseWebUrl } from 'lib/constants';
import { Availability } from 'lib/features/availability/types';
import { FareharborAvailability, Item } from "lib/features/item/types";
import { selectCurrentChannelUrl, selectIsExistingBookingLink, setIsExistingBookingLink } from 'lib/features/message/messageSlice';
import { CreateReferralDto } from 'lib/features/referral/referralService';
import { selectUser } from "lib/features/user/userSlice";
import moment from "moment";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import chatServices from 'services/chat-service';
import referralServices from 'services/referral-service';
import { availabilityScreenStyles } from "styles/AvailabilityDirectory/availabilityScreen.styles";
import FareharborIcon from "../../../assets/icons/fareharbor.svg";
import ShareAvailabilityModal from "./ShareAvailabilityModal";

interface FareharborAvailabilityCardProps {
  item: Item;
}


const FareharborAvailabilityCard = ({
  item,
}: FareharborAvailabilityCardProps) => {
  const isExistingBookingLink = useSelector(selectIsExistingBookingLink);
  const currentChannelUrl = useSelector(selectCurrentChannelUrl);
  const theme = useTheme();
  const fallbackImage = item.farehabor_item_details?.image_cdn_url || BoatImage;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const [loading, setLoading] = useState<boolean>(false);
  const [price, setPrice] = useState<number>(0);
  const [isShareModal, setIsShareModal] = useState<boolean>(false);
  const [isConfirmShareModal, setIsConfirmShareModal] = useState<boolean>(false);
  const [availability, setAvailability] = useState<FareharborAvailability>();


  const calculateDuration = (startTime: Date, endTime: Date): number => {
    const start = new Date(startTime);
    const end = new Date(endTime);
    let timeDifference = (end.getTime() - start.getTime()) / (1000 * 3600); // Difference in hours


    if (timeDifference < 0) {
      // Adjust the end time if the time difference is negative (crosses midnight)
      const correctedEndTime = new Date(end);
      correctedEndTime.setDate(correctedEndTime.getDate() + 1); // Move to the next day
      return calculateDuration(startTime, correctedEndTime);
    }


    if (timeDifference > 12) {
      timeDifference -= 12;
    }


    return timeDifference;
  };

  const refereeName = (() => {
    const name = decodeURIComponent(currentChannelUrl)
      ?.split("/")?.[2];

    const parts = name?.split(" ") ?? [];

    return parts.length > 1
      ? `${parts[0]} ${parts[1][0]}.`
      : parts[0];
  })()
  useEffect(() => {
    let calculatedPrice = Number(
      (item?.fareharbor_availabilities?.[0]?.customer_type_rates?.[0]
        ?.customer_prototype?.total ?? 0) / 100
    );


    if (item?.fareharbor_availabilities !== undefined) {
      try {
        calculatedPrice =
          item.fareharbor_availabilities[0]?.custom_field_instances.filter(
            (f: any) =>
              f.custom_field.type === "extended-option" &&
              f.custom_field.is_required
          )[0].custom_field.extended_options[0].total / 100;
      } catch { }
    }


    setPrice(calculatedPrice);
  }, [item]);

  const handleShare = async (selectedAvailability: Availability | FareharborAvailability) => {
    if (!user?.id || !selectedAvailability || !currentChannelUrl) {
      console.error("Missing required data: user, availability, or channel URL");
      return;
    }

    try {
      setLoading(true);

      let referralPayload: CreateReferralDto;

      // Internal availability (non-FareHarbor)
      if (item.item_type !== 4) {
        const internalAvailability = selectedAvailability as Availability;

        referralPayload = {
          user_id: user.id,
          business_id: item.business.id,
          availability_id: internalAvailability.id,
          item_id: item.id,
          referral_type: 1,
          state: 1,
        };
      } else {
        // FareHarbor availability
        const fhAvailability = selectedAvailability as FareharborAvailability;

        referralPayload = {
          user_id: user.id,
          business_id: item.business.id,
          referral_type: 4,
          external_availability_id: fhAvailability.pk,
          fareharbor_customer_prototype_id:
            fhAvailability.customer_type_rates?.[0]?.pk,
          item_id: item.id,
          state: 1,
        };
      }

      const referralResponse = await referralServices.createReferral(referralPayload);
      const referralId = referralResponse?.data?.id;

      if (!referralId) {
        throw new Error("Missing referral id from createReferral response");
      }

      const url = `${baseWebUrl}/checkout/${referralId}`;

      // Initialize Twilio client if needed and get the conversation
      const fetchToken = async () => {
        const res = await chatServices.getUserTokenForChat(String(user.id));
        return String(res.data.token);
      };

      const client = await getOrInitTwilioClient(fetchToken);
      // currentChannelUrl may contain full path: {sid}/{avatar}/{name}/{myavatar}
      // Extract just the SID for the API call
      const conversationSid = currentChannelUrl.split('/')[0];
      const conversation = await client.getConversationBySid(conversationSid);

      // Send the message directly via Twilio SDK
      await conversation.sendMessage(url);
      dispatch(setIsExistingBookingLink(false));
      // Navigate back to the chat
      navigate(`/chat/${currentChannelUrl}`);
    } catch (error) {
      console.error("Error sending message to conversation:", error);
    } finally {
      setLoading(false);
    }
  };


  return (
    <Box sx={availabilityScreenStyles.card(theme)}>
      {/* Image Box */}
      <Box sx={availabilityScreenStyles.imageBox}>
        <img
          src={fallbackImage}
          alt={"image"}
          style={availabilityScreenStyles.image}
        />
      </Box>


      {/* Body */}
      <Box p={2}>
        <Box>
          <Box sx={availabilityScreenStyles.headerRow}>
            <Typography style={availabilityScreenStyles.titleTextFareharbor}>
              {item?.farehabor_item_details?.name}
            </Typography>
            <img
              src={FareharborIcon}
              style={availabilityScreenStyles.fareharborIcon}
            />
            {/* Availability Section */}
            <Box sx={availabilityScreenStyles.availabilityBadge}>
              <Typography sx={[availabilityScreenStyles.availabilityText, { textWrap: 'nowrap' }]} fontSize="11px">
                {item?.fareharbor_availabilities?.[0]?.capacity} Available
              </Typography>
            </Box>
          </Box>


          <Typography sx={availabilityScreenStyles.subtitleText}>
            {item?.farehabor_item_details?.headline}
          </Typography>
        </Box>


        {/* Details Section */}
        {item.fareharbor_availabilities !== undefined &&
          item?.fareharbor_availabilities?.length > 0 &&
          item?.fareharbor_availabilities.map((data, index) => (
            <Box key={data.pk} sx={availabilityScreenStyles.detailContainer}>
              {/* First Row */}
              <Box sx={availabilityScreenStyles.rowContainer}>
                {/* Time and Duration section */}
                <Box
                  sx={availabilityScreenStyles.timeContainer}
                  onClick={() => {
                    // setAvailability(data);
                  }}
                >
                  <Typography style={availabilityScreenStyles.rowText}>
                    {moment
                      .utc(data?.start_at)
                      .tz("America/New_York")
                      .format("hh:mm A")}
                  </Typography>
                  <Typography
                    sx={[
                      availabilityScreenStyles.rowText,
                      availabilityScreenStyles.dotText,
                    ]}
                  >
                    •
                  </Typography>
                  <Typography sx={availabilityScreenStyles.rowText}>
                    {calculateDuration(
                      new Date(data.start_at),
                      new Date(data.end_at)
                    ) + " hr"}
                  </Typography>
                </Box>
                <Box sx={availabilityScreenStyles.priceSection}>
                  <Typography sx={availabilityScreenStyles.priceLabel}>
                    Price:
                  </Typography>
                  <Typography sx={availabilityScreenStyles.priceValue}>
                    $ {Number(price)}
                  </Typography>
                </Box>


                {/* Price Section 2 */}
                <Box sx={availabilityScreenStyles.priceSection}>
                  <Typography sx={availabilityScreenStyles.priceLabel}>
                    Pays:
                  </Typography>
                  <Typography sx={availabilityScreenStyles.priceValue}>
                    {`${item?.commission}%`} (
                    {`$${(price * (item?.commission || 0)) / 100}`})
                  </Typography>
                </Box>
                {
                  currentChannelUrl && (
                    <Box sx={availabilityScreenStyles.priceSection}>
                      <Typography sx={availabilityScreenStyles.priceLabel}>
                        Referee :
                      </Typography>
                      <Typography sx={availabilityScreenStyles.priceValue}>
                        {refereeName}
                      </Typography>
                    </Box>
                  )
                }
              </Box>



              <Box display="flex" justifyContent="center" mt={3}>
                {
                  loading ? (
                    <Button variant="contained" disabled sx={{ width: "174px", height: "44px", "&.Mui-disabled": { backgroundColor: "#235e24" } }}>
                      <CircularProgress size={20} sx={{ color: "#fff" }} />
                    </Button>
                  ) : (
                    <Button variant="contained" sx={{ width: "174px", height: "44px" }} startIcon={<IosShareIcon />} onClick={() => {
                      if (isExistingBookingLink) {
                        handleShare(data);
                        setLoading(true)
                      } else {
                        setIsShareModal(true);
                      }
                      setAvailability(data);
                    }}>
                      Share
                    </Button>
                  )
                }
              </Box>
            </Box>
          ))}
      </Box>


      {availability && (
        <ShareAvailabilityModal
          open={isShareModal}
          close={() => setIsShareModal(false)}
          selectedAvailability={availability}
          item={item}
        />
      )}
    </Box>
  );
};


export default FareharborAvailabilityCard;